import React, { useState } from 'react';
import { ArgumentPair, TxInspection } from '../../../../../../../../types/rest/analyticsTypes';
import _ from 'lodash';
import { Button, ButtonGroup, Tab, Tabs } from 'react-bootstrap';
import BlazeTable from '../../../../../../../../components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from '../../../../../../../../components/blazeTable/BlazeTable.types';
import { isBase64 } from '../../../../../../../../utils/miscUtil';
import i18n from '../../../../../../../../i18n';
import ExpandableBody from '../../../../../../../Analytics/AnalyticsMockService/Common/ExpandableBody';

interface Props {
  txDslDetails: TxInspection;
  requestArgs: ArgumentPair[];
}

const HttpTransactionDslDetail = ({ txDslDetails, requestArgs }: Props) => {
  const [activeTab, setActiveTab] = useState('request');

  const getButtonGroup = () => {
    return (
      <div>
        <ButtonGroup className={'mb-2 mt-2'}>
          <Button
            key={'request'}
            onClick={() => {
              setActiveTab('request');
            }}
            className={'show-request-btn'}
            variant={activeTab === 'request' ? 'primary' : 'secondary'}
          >
            Request
          </Button>
          <Button
            key={'response'}
            onClick={() => {
              setActiveTab('response');
            }}
            className={'show-response-btn'}
            variant={activeTab === 'response' ? 'primary' : 'secondary'}
          >
            Response
          </Button>
        </ButtonGroup>
      </div>
    );
  };

  const getHeadersTableOptions = (table: 'headers' | 'properties'): BlazeTableOptions => ({
    showRowExpander: false,
    columnDef: [
      //Accessor differs by the table type
      { header: 'Name', accessor: table === 'headers' ? 'key' : 'name', width: 360 },
      { header: 'Value', accessor: 'value' },
    ],
    pageable: true,
    selectable: false,
    pageSize: 10,
    tableName: 'eventList',
    noResultsText: 'No items available',
    customHeaderClassName: 'flex-table-item-top',
    customTableClassName: 'flex-table-simple flex-table-condensed',
  });

  const getEncodedBody = (body: string) => {
    if (isBase64(body)) {
      return Buffer.from(body, 'base64').toString();
    } else {
      return body;
    }
  };

  const getColumnsDefinition = (useType: boolean) => {
    // Definition of table columns
    const colsDefinition: BlazeTableColDef[] = [
      {
        header: i18n.t('analytics:txInspection.detail.name'),
        width: 30,
        accessor: 'key',
      },
      {
        header: i18n.t('analytics:txInspection.detail.value'),
        width: useType ? 40 : 70,
        accessor: 'value',
      },
    ];

    if (useType) {
      colsDefinition.push({
        header: i18n.t('analytics:txInspection.detail.type'),
        width: 30,
        accessor: 'type',
      });
    }
    return colsDefinition;
  };

  const getTableOptions = (useType: boolean, noResultsText?: string) => {
    const tableOptions: BlazeTableOptions = {
      showRowExpander: false,
      columnDef: getColumnsDefinition(useType),
      pageable: true,
      selectable: false,
      pageSize: 5,
      minWidth: 950,
      minHeight: 150,
      tableName: 'eventList',
      noResultsText: noResultsText ? noResultsText : i18n.t('analytics:txInspection.table.noResults'),
      customHeaderClassName: 'flex-table-item-top',
      customTableClassName: 'flex-table-simple flex-table-condensed',
      maxContentHeight: '185px',
    };
    return tableOptions;
  };

  const getRequestDetail = () => {
    return (
      <>
        <Tabs>
          <Tab eventKey="Header" title={'Headers (' + txDslDetails.request.header.length + ')'}>
            <BlazeTable
              options={getTableOptions(false)}
              data={txDslDetails.request.header}
              showPageSizeDropdown={true}
            />
          </Tab>
          {requestArgs && requestArgs.length > 0 && (
            <Tab eventKey="QueryParams" title={'Query Parameters (' + requestArgs.length + ')'}>
              <BlazeTable
                options={getTableOptions(false, 'No Parameters available')}
                data={requestArgs}
                showPageSizeDropdown={true}
              />
            </Tab>
          )}
          <Tab eventKey="body" title={'Body'}>
            <label className="d-inline-block mb-3">{i18n.t('analytics:txInspection.detail.bodyContent')}</label>
            {txDslDetails.request.bodyContent ? (
              <div className={'position-relative'} style={{ bottom: '22px' }}>
                <ExpandableBody expandedHeight={1000} height={360}>
                  {txDslDetails.request.bodyContent}
                </ExpandableBody>
              </div>
            ) : (
              <p>{i18n.t('analytics:txInspection.detail.noBodyContentAvailable')}</p>
            )}
          </Tab>
        </Tabs>
      </>
    );
  };

  const getResponseDetail = () => {
    return (
      <>
        <Tabs>
          <Tab eventKey="Header" title={'Headers (' + txDslDetails.response.header.length + ')'}>
            <BlazeTable
              options={getTableOptions(false)}
              data={txDslDetails.response.header}
              showPageSizeDropdown={true}
            />
          </Tab>
          <Tab eventKey="body" title={'Body'}>
            <label className="d-inline-block mb-3">{i18n.t('analytics:txInspection.detail.bodyContent')}</label>
            {txDslDetails.response.bodyContent ? (
              <div className={'position-relative'} style={{ bottom: '22px' }}>
                <ExpandableBody expandedHeight={1000} height={360}>
                  {txDslDetails.response.bodyContent}
                </ExpandableBody>
              </div>
            ) : (
              <p>{i18n.t('analytics:txInspection.detail.noBodyContentAvailable')}</p>
            )}
          </Tab>
        </Tabs>
      </>
    );
  };

  return (
    <div>
      {_.isNil(txDslDetails) ? (
        <div>Message Detail not available</div>
      ) : (
        <div>
          {getButtonGroup()}
          {activeTab === 'request' && <>{getRequestDetail()}</>}
          {activeTab === 'response' && <>{getResponseDetail()}</>}
        </div>
      )}
    </div>
  );
};

export default HttpTransactionDslDetail;
